import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_ALL_MEAL_PLANS,
  CREATE_MEAL_PLAN,
  UPDATE_MEAL_PLAN,
  DELETE_MEAL_PLAN
} from '../graphql/mealPlans';
import {
  TrashIcon,
  PencilIcon,
  PlusIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import ManagementModal from '../components/ManagementModal';
import axios from 'axios';

const MealPlansManagement = () => {
  const { isDarkMode } = useTheme();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mealPlanData, setMealPlanData] = useState({
    name: '',
    mealType: '',
    protein: '',
    carbs: '',
    fats: '',
    total_kcal: '',
    content: '',
    expertAdvice: ''
  });
  const [editingMealPlan, setEditingMealPlan] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  // Fetch all meal plans
  const { loading, error, data, refetch } = useQuery(GET_ALL_MEAL_PLANS, {
    onError: (error) => handleGraphQLError(error, 'Failed to load meal plans')
  });

  // Mutations
  const [createMealPlan] = useMutation(CREATE_MEAL_PLAN, {
    onCompleted: async (data) => {
      // Get the ID of the created meal plan
      const mealPlanId = data.createMealPlan.id;

      // Upload thumbnail if selected
      if (selectedThumbnail) {
        try {
          const formData = new FormData();
          formData.append('mealPlanId', mealPlanId);
          formData.append('thumbnail', selectedThumbnail);

          const baseUrl = process.env.REACT_APP_API_BASE_URL;
          const uploadUrl = `${baseUrl}/meal-plan/upload`;
          await axios.post(uploadUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            }
          });

          toast.success('Thumbnail uploaded successfully');
        } catch (uploadError) {
          toast.error('Failed to upload thumbnail');
          console.error('Thumbnail upload error:', uploadError);
        }
      }

      // Reset form and state
      resetForm();
      refetch();
      toast.success('Meal plan created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create meal plan')
  });

  const [updateMealPlan] = useMutation(UPDATE_MEAL_PLAN, {
    onCompleted: () => {
      resetForm();
      refetch();
      toast.success('Meal plan updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update meal plan')
  });

  const [deleteMealPlan] = useMutation(DELETE_MEAL_PLAN, {
    onCompleted: () => {
      refetch();
      toast.success('Meal plan deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete meal plan')
  });

  const resetForm = () => {
    setMealPlanData({
      name: '',
      mealType: '',
      protein: '',
      carbs: '',
      fats: '',
      total_kcal: '',
      content: '',
      expertAdvice: ''
    });
    setEditingMealPlan(null);
    setIsModalOpen(false);
    setSelectedThumbnail(null);
    setThumbnailPreview(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    const requiredFields = ['name', 'mealType', 'protein', 'carbs', 'fats', 'total_kcal', 'content'];
    const missingFields = requiredFields.filter(field => !mealPlanData[field]);

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    const input = {
      ...mealPlanData,
      mealType: mealPlanData.mealType.charAt(0).toUpperCase() + mealPlanData.mealType.slice(1).toLowerCase(),
      protein: mealPlanData.protein.toString(),
      carbs: mealPlanData.carbs.toString(),
      fats: mealPlanData.fats.toString(),
      total_kcal: mealPlanData.total_kcal.toString(),
      content: mealPlanData.content,
      expertAdvice: mealPlanData.expertAdvice || ''
    };

    if (editingMealPlan) {
      updateMealPlan({
        variables: {
          updateMealPlanId: editingMealPlan.id,
          input
        }
      });
    } else {
      createMealPlan({
        variables: { input }
      });
    }
  };

  const handleEdit = (mealPlan) => {
    setEditingMealPlan(mealPlan);
    setMealPlanData({
      name: mealPlan.name || '',
      mealType: mealPlan.mealType ? mealPlan.mealType.toLowerCase() : '',
      protein: mealPlan.protein.toString(),
      carbs: mealPlan.carbs.toString(),
      fats: mealPlan.fats.toString(),
      total_kcal: mealPlan.total_kcal.toString(),
      content: mealPlan.content,
      expertAdvice: mealPlan.expertAdvice || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = (mealPlanId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this meal plan?');
    if (confirmDelete) {
      deleteMealPlan({
        variables: { deleteMealPlanId: mealPlanId }
      });
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    setSelectedThumbnail(file);

    // Create a preview URL
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
    } else {
      setThumbnailPreview(null);
    }
  };

  // Loading and error states
  if (loading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading meal plans...
    </div>
  );

  if (error) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading meal plans
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Add Meal Plan Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => {
            setEditingMealPlan(null);
            setIsModalOpen(true);
          }}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg
            ${isDarkMode
              ? 'bg-blue-600 text-white hover:bg-blue-500'
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
            transition
          `}
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Meal Plan</span>
        </button>
      </div>

      {/* Meal Plans Management Modal */}
      <ManagementModal
        isOpen={isModalOpen}
        onClose={() => {
          resetForm();
          setIsModalOpen(false);
        }}
        onSubmit={handleSubmit}
        title={editingMealPlan ? 'Edit Meal Plan' : 'Create New Meal Plan'}
      >
        {/* Name Input */}
        <div>
          <label
            htmlFor="name"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Meal Plan Name
          </label>
          <input
            type="text"
            id="name"
            value={mealPlanData.name}
            onChange={(e) => setMealPlanData({ ...mealPlanData, name: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter meal plan name"
            required
          />
        </div>

        {/* Meal Type Input */}
        <div>
          <label
            htmlFor="mealType"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Meal Type
          </label>
          <select
            id="mealType"
            value={mealPlanData.mealType}
            onChange={(e) => setMealPlanData({ ...mealPlanData, mealType: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            required
          >
            <option value="">Select Meal Type</option>
            <option value="breakfast">Breakfast</option>
            <option value="lunch">Lunch</option>
            <option value="dinner">Dinner</option>
            <option value="snack">Snack</option>
          </select>
        </div>

        {/* Protein Input */}
        <div>
          <label
            htmlFor="protein"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Protein (g)
          </label>
          <input
            type="number"
            id="protein"
            value={mealPlanData.protein}
            onChange={(e) => setMealPlanData({ ...mealPlanData, protein: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter protein amount"
            required
          />
        </div>

        {/* Carbs Input */}
        <div>
          <label
            htmlFor="carbs"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Carbs (g)
          </label>
          <input
            type="number"
            id="carbs"
            value={mealPlanData.carbs}
            onChange={(e) => setMealPlanData({ ...mealPlanData, carbs: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter carbs amount"
            required
          />
        </div>

        {/* Fats Input */}
        <div>
          <label
            htmlFor="fats"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Fats (g)
          </label>
          <input
            type="number"
            id="fats"
            value={mealPlanData.fats}
            onChange={(e) => setMealPlanData({ ...mealPlanData, fats: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter fats amount"
            required
          />
        </div>

        {/* Total Calories Input */}
        <div>
          <label
            htmlFor="total_kcal"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Total Calories
          </label>
          <input
            type="number"
            id="total_kcal"
            value={mealPlanData.total_kcal}
            onChange={(e) => setMealPlanData({ ...mealPlanData, total_kcal: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter total calories"
            required
          />
        </div>

        {/* Content Input */}
        <div>
          <label
            htmlFor="content"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Meal Content
          </label>
          <textarea
            id="content"
            value={mealPlanData.content}
            onChange={(e) => setMealPlanData({ ...mealPlanData, content: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Describe the meal content"
            rows={3}
            required
          />
        </div>

        {/* Expert Advice Input (Optional) */}
        <div>
          <label
            htmlFor="expertAdvice"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Expert Advice (Optional)
          </label>
          <textarea
            id="expertAdvice"
            value={mealPlanData.expertAdvice}
            onChange={(e) => setMealPlanData({ ...mealPlanData, expertAdvice: e.target.value })}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500'
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Add any expert nutritional advice"
            rows={3}
          />
        </div>

        {/* Thumbnail Upload */}
        <div>
          <label
            htmlFor="thumbnailUpload"
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Thumbnail
          </label>
          <input
            type="file"
            id="thumbnailUpload"
            accept="image/*"
            onChange={handleThumbnailChange}
            className={`
              w-full px-3 py-2 
              ${isDarkMode
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
              }
              border rounded-md
            `}
          />
          {thumbnailPreview && (
            <div className="mt-2">
              <img
                src={thumbnailPreview}
                alt="Thumbnail Preview"
                className="w-32 h-32 object-cover rounded-md"
              />
            </div>
          )}
        </div>
      </ManagementModal>

      {/* Meal Plans List */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Meal Plans List
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {data?.getAllMealPlans?.length || 0} meal plans
          </div>
        </div>

        {/* Meal Plans Table */}
        <div className="overflow-x-auto">
          {data.getAllMealPlans.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No meal plans found
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className={`
                ${isDarkMode
                  ? 'bg-gray-700 text-gray-300'
                  : 'bg-gray-100 text-gray-600'
                } 
                text-sm
              `}>
                <tr>
                  <th className="px-6 py-3 text-left">Thumbnail</th>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Meal Type</th>
                  <th className="px-6 py-3 text-left">Protein (g)</th>
                  <th className="px-6 py-3 text-left">Carbs (g)</th>
                  <th className="px-6 py-3 text-left">Fats (g)</th>
                  <th className="px-6 py-3 text-left">Total Calories</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-left">Updated At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.getAllMealPlans.map((mealPlan) => (
                  <tr
                    key={mealPlan.id}
                    className={`
                      border-b 
                      ${isDarkMode
                        ? 'border-gray-700 hover:bg-gray-700'
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className="px-6 py-4">
                      {mealPlan.thumbnailURL ? (
                        <img
                          src={mealPlan.thumbnailURL.startsWith('http')
                            ? mealPlan.thumbnailURL
                            : `${process.env.REACT_APP_API_BASE_URL}${mealPlan.thumbnailURL}`
                          }
                          alt={`${mealPlan.mealType} thumbnail`}
                          className="w-16 h-16 object-cover rounded-md"
                        />
                      ) : (
                        <div className={`
                          w-16 h-16 flex items-center justify-center 
                          rounded-md 
                          ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}
                        `}>
                          <span className={`
                            text-xs 
                            ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                          `}>
                            No Image
                          </span>
                        </div>
                      )}
                    </td>
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {mealPlan.name || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mealPlan.mealType.charAt(0).toUpperCase() + mealPlan.mealType.slice(1)}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mealPlan.protein}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mealPlan.carbs}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mealPlan.fats}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {mealPlan.total_kcal}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(mealPlan.createdAt)}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(mealPlan.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(mealPlan)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700'
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(mealPlan.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700'
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default MealPlansManagement;
